import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("bg-BG", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function AdminDimovskySync() {
  const [overview, setOverview] = useState(null);
  const [mapping, setMapping] = useState([]);
  const [logs, setLogs] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    api.get("/admin/dimovsky/overview").then(({ data }) => setOverview(data));
    api.get("/admin/dimovsky/redirect-map").then(({ data }) => setMapping(data));
    api.get("/admin/dimovsky/sync-log").then(({ data }) => setLogs(data));
  };

  useEffect(load, []);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/dimovsky/sync-now");
      toast.success(`Синхронизирано: ${data.imported} нови, ${data.updated} обновени, ${data.skipped_manual_edit} пропуснати (ръчно редактирани)`);
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSyncing(false);
    }
  };

  const copyMapping = async () => {
    const text = mapping.map((m) => `${m.source_url} -> ${m.metri_url}`).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Таблицата с redirect-и е копирана");
    } catch {
      toast.error("Копирането не бе разрешено от браузъра. Избери текста ръчно.");
    }
  };

  return (
    <div data-testid="admin-dimovsky-sync-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl font-bold text-[#1A1A1A]">Dimovsky.bg синхронизация</h1>
        <Button onClick={syncNow} disabled={syncing} className="bg-[#A6192E] hover:bg-[#8C1123]" data-testid="admin-dimovsky-sync-now-btn">
          {syncing ? "Синхронизиране..." : "Синхронизирай сега"}
        </Button>
      </div>

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10" data-testid="admin-dimovsky-overview">
          <Stat label="Открити статии" value={overview.total_discovered} />
          <Stat label="Внесени в Metri.bg" value={overview.total_imported} />
          <Stat label="Конфигурирани redirect-и" value={overview.redirects_configured} />
          <Stat label="Нужно внимание" value={overview.needs_attention} warn={overview.needs_attention > 0} />
          <Stat label="Последна синхронизация" value={overview.last_sync_at ? formatDateTime(overview.last_sync_at) : "—"} small />
        </div>
      )}

      <div className="bg-white border border-[#E2E8F0] p-6 mb-10">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold text-[#1A1A1A]">301 redirect таблица (за прилагане на dimovsky.bg)</h2>
          <Button size="sm" variant="outline" onClick={copyMapping} data-testid="admin-dimovsky-copy-mapping-btn">Копирай всички</Button>
        </div>
        <p className="text-xs text-[#718096] mb-4">
          METRI.bg няма достъп до хостинга на dimovsky.bg. Тази таблица трябва да се приложи като 301 redirect правила на dimovsky.bg страната.
        </p>
        <div className="max-h-96 overflow-y-auto divide-y divide-[#E2E8F0]" data-testid="admin-dimovsky-mapping-table">
          {mapping.map((m) => (
            <div key={m.id} className="py-2 text-xs font-mono flex flex-col gap-0.5">
              <span className="text-[#718096]">{m.source_url}</span>
              <span className="text-[#166534]">&rarr; {m.metri_url}</span>
            </div>
          ))}
          {mapping.length === 0 && <p className="text-sm text-[#718096] py-4">Няма данни все още.</p>}
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-6">
        <h2 className="font-semibold text-[#1A1A1A] mb-4">История на синхронизациите</h2>
        <div className="divide-y divide-[#E2E8F0]" data-testid="admin-dimovsky-sync-log-table">
          {logs.map((l) => (
            <details key={l.id} className="py-3 text-sm" data-testid={`sync-log-entry-${l.id}`}>
              <summary className="flex items-center justify-between cursor-pointer list-none">
                <div>
                  <span className={`font-semibold ${l.status === "ok" ? "text-[#166534]" : l.status === "error" ? "text-red-600" : "text-[#A6192E]"}`}>{l.status}</span>
                  <span className="text-[#718096]"> &middot; {formatDateTime(l.created_at)} &middot; {l.run_type}</span>
                </div>
                <div className="text-xs text-[#718096]">
                  нови: {l.imported} &middot; обновени: {l.updated} &middot; пропуснати: {l.skipped_manual_edit} &middot; грешки: {l.failed}
                </div>
              </summary>
              {l.details?.length > 0 && (
                <ul className="mt-2 pl-4 list-disc text-xs text-[#718096] space-y-1" data-testid={`sync-log-details-${l.id}`}>
                  {l.details.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              )}
            </details>
          ))}
          {logs.length === 0 && <p className="text-sm text-[#718096]">Все още няма синхронизации.</p>}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, warn, small }) {
  return (
    <div className="bg-white border border-[#E2E8F0] p-4">
      <div className={`font-serif font-bold text-[#1A1A1A] ${small ? "text-sm" : "text-2xl"} ${warn ? "text-[#A6192E]" : ""}`}>{value}</div>
      <div className="text-xs text-[#718096] mt-1">{label}</div>
    </div>
  );
}
